import { SnipcartCart, SnipcartProduct } from "@type/snipcart"
import getConfig from "next/config"
import Script from "next/script"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react"

type StateProps = {
  ready: boolean
  userStatus: string
  cartQuantity: number
  cartTotal: number
  cartSubTotal: number
}

const initialState: StateProps = {
  ready: false,
  userStatus: "SignedOut",
  cartQuantity: 0,
  cartTotal: 0,
  cartSubTotal: 0,
}

const SnipcartContext = createContext(initialState)

export const SnipcartProvider = (props: {
  children: ReactNode
  defaultLang: string
}) => {
  const { state, dispatch } = useSnipcartStore()
  const { children, defaultLang } = props

  const changeLanguage = useCallback(
    (lang: string) => {
      const locales = {
        en: {
          cart: {
            secured_by: "Secured Payments",
            shipping_taxes_calculated_at_checkout: "",
          },
          cart_summary: {
            taxes_included_in_price: "5% VAT included in price",
          },
          discount_box: {
            promo_code: "Apply Promo Code",
          },
          payment: {
            methods: {
              deferred_payment: "Cash on Delivery",
            },
            form: {
              deferred_payment_title: "Cash on Delivery",
              deferred_payment_instructions:
                "Pay in cash at the time of the actual delivery of the product at your doorstep.",
            },
          },
        },
      }
      const lng = locales[defaultLang as keyof typeof locales] || {}
      window.Snipcart.api.session.setLanguage(lang, lng)
    },
    [defaultLang]
  )

  useEffect(() => {
    const listenSnipcart = () => {
      document.addEventListener("snipcart.ready", () => {
        dispatch({ type: "setReady", payload: true })
        changeLanguage(defaultLang)
      })
    }

    if (window.Snipcart !== undefined) {
      dispatch({ type: "setReady", payload: true })
      changeLanguage(defaultLang)
    } else {
      listenSnipcart()
    }
  }, [dispatch, defaultLang, changeLanguage])

  // subscribe to snipcart events
  useEffect(() => {
    const listenSnipcart = () => {
      // console.info("snipcart is ready")
      // // Subscribing to different events

      window.Snipcart.events.on(
        "item.adding",
        (parsedItem: SnipcartProduct) => {
          // console.log("item.adding", parsedItem)
          window.zaraz?.track("Product Adding", parsedItem)
        }
      )

      window.Snipcart.events.on("item.added", (cartItem: SnipcartProduct) => {
        // console.log("item.added", cartItem)
        window.zaraz?.ecommerce("Product Added", {
          product_id: cartItem.id,
          sku: cartItem.id,
          category: cartItem.categories?.[0],
          name: cartItem.name,
          variant: cartItem.metadata?.Format,
          price: cartItem.price,
          currency: "AED",
          quantity: cartItem.quantity,
        })
      })

      window.Snipcart.events.on("item.updated", (cartItem: SnipcartProduct) => {
        // console.log("item.updated", cartItem)
        window.zaraz?.track("Product Updated", cartItem)
      })

      window.Snipcart.events.on("item.removed", (cartItem: SnipcartProduct) => {
        // console.log("item.removed", cartItem)
        window.zaraz?.ecommerce("Product Removed", {
          product_id: cartItem.id,
          sku: cartItem.id,
          category: cartItem.categories?.[0],
          name: cartItem.name,
          variant: cartItem.metadata?.Format,
          price: cartItem.price,
          currency: "AED",
          quantity: cartItem.quantity,
        })
      })

      window.Snipcart.events.on("cart.created", (cartState) => {
        // console.log("cart.created", cartState)
        window.zaraz?.track("Cart Created", cartState)
      })

      window.Snipcart.events.on("cart.confirmed", (cartState: SnipcartCart) => {
        const hasDiscount = cartState.discounts?.items.length > 0
        // console.log("cart.confirmed", cartState)

        window.zaraz?.ecommerce("Order Completed", {
          checkout_id: cartState.token,
          order_id: cartState.invoiceNumber,
          total: cartState.total,
          revenue: cartState.total - cartState.taxes.items[0].amount,
          shipping: 0,
          tax: cartState.taxes.items[0].amount,
          discount: hasDiscount ? cartState.discounts.items[0].value : 0,
          coupon: hasDiscount ? cartState.discounts.items[0].name : "",
          currency: "AED",
          products: cartState.items.items.map((item) => ({
            product_id: item.id,
            sku: item.uniqueId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            category: item.categories?.[0],
          })),
        })
      })

      window.Snipcart.events.on("cart.confirm.error", (confirmError) => {
        // console.log("cart.confirm.error", confirmError)
        window.zaraz?.track("Cart Confirm Error", confirmError)
      })

      window.Snipcart.events.on("cart.reset", (cartState) => {
        // console.log("cart.reset", cartState)
        window.zaraz?.track("Cart Reset", cartState)
      })

      window.Snipcart.events.on("payment.failed", (paymentError) => {
        // console.log("payment.failed", paymentError)
        window.zaraz?.track("Payment Failed", paymentError)
      })

      window.Snipcart.events.on("discount.applied", (discount) => {
        // console.log("discount.applied", discount)
        window.zaraz?.track("Discount Applied", discount)
      })

      window.Snipcart.events.on("shipping.selected", (shippingMethod) => {
        // console.log("shipping.selected", shippingMethod)
        window.zaraz?.track("Shipping Selected", shippingMethod)
      })

      window.Snipcart.events.on("customer.registered", (customer) => {
        // console.log("customer.registered", customer)
        window.zaraz?.track("Customer Registered", customer)
      })

      window.Snipcart.events.on("customer.signedin", (customer) => {
        // console.log("customer.signedin", customer)
        window.zaraz?.track("Customer Signed In", customer)
      })

      // window.Snipcart.events.on("customer.signedout", () => {
      //   console.log("customer.signedout")
      // })

      // window.Snipcart.events.on("snipcart.initialized", (snipcartState) => {
      //   console.log("snipcart.initialized", snipcartState)
      // })

      // window.Snipcart.events.on("snipcart.initialization.error", () => {
      //   console.log("snipcart.initialization.error")
      // })

      // window.Snipcart.events.on("summary.checkout_clicked", () => {
      //   console.log("summary.checkout_clicked")
      // })
    }

    document.addEventListener("snipcart.ready", listenSnipcart)

    return () => {
      document.removeEventListener("snipcart.ready", listenSnipcart)
    }
  }, [])

  const { publicRuntimeConfig } = getConfig()
  const version = publicRuntimeConfig?.version

  return (
    <>
      <Script
        id={"snipcart"}
        strategy={"afterInteractive"}
        type={"text/javascript"}
        dangerouslySetInnerHTML={{
          __html: `
                window.SnipcartSettings = {
                  language: "en",
                  publicApiKey: "${process.env.NEXT_PUBLIC_SNIPCART_API_KEY}",
                  loadStrategy: "on-user-interaction",
                  modalStyle: "side",
                  timeoutDuration: 5000,
                  currency: "AED",
                  addProductBehavior: "none",
                  templatesUrl: "/snipcart/snipcart-templates.html?v=${version}",
                };
        
                (function(){var c,d;(d=(c=window.SnipcartSettings).version)!=null||(c.version="3.0");var s,S;(S=(s=window.SnipcartSettings).currency)!=null||(s.currency="usd");var l,p;(p=(l=window.SnipcartSettings).timeoutDuration)!=null||(l.timeoutDuration=2750);var w,u;(u=(w=window.SnipcartSettings).domain)!=null||(w.domain="cdn.snipcart.com");var m,g;(g=(m=window.SnipcartSettings).protocol)!=null||(m.protocol="https");var f,v;(v=(f=window.SnipcartSettings).loadCSS)!=null||(f.loadCSS=!0);var E=window.SnipcartSettings.version.includes("v3.0.0-ci")||window.SnipcartSettings.version!="3.0"&&window.SnipcartSettings.version.localeCompare("3.4.0",void 0,{numeric:!0,sensitivity:"base"})===-1,y=["focus","mouseover","touchmove","scroll","keydown"];window.LoadSnipcart=o;document.readyState==="loading"?document.addEventListener("DOMContentLoaded",r):r();function r(){window.SnipcartSettings.loadStrategy?window.SnipcartSettings.loadStrategy==="on-user-interaction"&&(y.forEach(function(t){return document.addEventListener(t,o)}),setTimeout(o,window.SnipcartSettings.timeoutDuration)):o()}var a=!1;function o(){if(a)return;a=!0;let t=document.getElementsByTagName("head")[0],n=document.querySelector("#snipcart"),i=document.querySelector('src[src^="'.concat(window.SnipcartSettings.protocol,"://").concat(window.SnipcartSettings.domain,'"][src$="snipcart.js"]')),e=document.querySelector('link[href^="'.concat(window.SnipcartSettings.protocol,"://").concat(window.SnipcartSettings.domain,'"][href$="snipcart.css"]'));n||(n=document.createElement("div"),n.id="snipcart",n.setAttribute("hidden","true"),document.body.appendChild(n)),$(n),i||(i=document.createElement("script"),i.src="".concat(window.SnipcartSettings.protocol,"://").concat(window.SnipcartSettings.domain,"/themes/v").concat(window.SnipcartSettings.version,"/default/snipcart.js"),i.async=!0,t.appendChild(i)),!e&&window.SnipcartSettings.loadCSS&&(e=document.createElement("link"),e.rel="stylesheet",e.type="text/css",e.href="".concat(window.SnipcartSettings.protocol,"://").concat(window.SnipcartSettings.domain,"/themes/v").concat(window.SnipcartSettings.version,"/default/snipcart.css"),t.prepend(e)),y.forEach(function(h){return document.removeEventListener(h,o)})}function $(t){!E||(t.dataset.apiKey=window.SnipcartSettings.publicApiKey,window.SnipcartSettings.addProductBehavior&&(t.dataset.configAddProductBehavior=window.SnipcartSettings.addProductBehavior),window.SnipcartSettings.modalStyle&&(t.dataset.configModalStyle=window.SnipcartSettings.modalStyle),window.SnipcartSettings.currency&&(t.dataset.currency=window.SnipcartSettings.currency),window.SnipcartSettings.templatesUrl&&(t.dataset.templatesUrl=window.SnipcartSettings.templatesUrl))}})();
          `,
        }}
      />
      <SnipcartContext.Provider value={state}>
        {children}
      </SnipcartContext.Provider>
    </>
  )
}

type ActionProps =
  | {
      type: "setReady"
      payload: boolean
    }
  | {
      type: "setQuantity"
      payload: number
    }
  | {
      type: "setUserStatus"
      payload: string
    }
  | {
      type: "setTotal"
      payload: number
    }
  | {
      type: "setSubTotal"
      payload: number
    }

const reducer = (state: StateProps, action: ActionProps) => {
  switch (action.type) {
    case "setReady":
      return { ...state, ...{ ready: action.payload } }
    case "setQuantity":
      return { ...state, ...{ cartQuantity: action.payload } }
    case "setUserStatus":
      return { ...state, ...{ userStatus: action.payload } }
    case "setTotal":
      return { ...state, ...{ cartTotal: action.payload } }
    case "setSubTotal":
      return { ...state, ...{ cartSubTotal: action.payload } }
    default:
      return state
  }
}

const useSnipcartStore = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  // update state
  useEffect(() => {
    const { Snipcart } = window
    if (Snipcart && state.ready === true) {
      // update state infos on change
      const listenSnipcart = () => {
        const { customer, cart } = Snipcart.store.getState()
        dispatch({
          type: "setQuantity",
          payload: cart.items.count,
        })
        dispatch({
          type: "setUserStatus",
          payload: customer.status,
        })
        dispatch({
          type: "setTotal",
          payload: cart.total,
        })
        dispatch({
          type: "setSubTotal",
          payload: cart.subtotal,
        })
      }
      // listen store update
      const unsubscribe = Snipcart.store.subscribe(listenSnipcart)
      // call first
      listenSnipcart()
      return () => unsubscribe()
    }
  }, [state.ready, dispatch])

  return { state: state, dispatch: dispatch }
}

export const useSnipcart = () => useContext(SnipcartContext)

export const hasSnipcart = () =>
  typeof window !== "undefined" && typeof window.Snipcart !== "undefined"
